// @flow
import { useState } from "react";
import { useMutation } from "@apollo/client/react/hooks";
import { useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { gql } from "@apollo/client";
import { sendAnalyticsEvent } from "@nested/analytics";
import { AVM_SUCCESSFUL_VALUATION } from "@nested/analytics/events";
import { Sentry } from "@nested/isomorphic-sentry";
import {
  type State,
  formStateUpdateAction,
} from "../components/MultiStageForm/ducks";
import { MultiStageForm, Question } from "../components/MultiStageForm";
import {
  Address,
  ContactDetails,
  ValuationIntent,
  ValuationUrgency,
  PropertyDetails,
} from "../questions";
import { calculateAvmLeadScore } from "../utils/sharedAvm";
import { usePersonalInformationOnSubmit } from "./usePersonalInformationOnSubmit";

const FORM_TYPE = "get-online-valuation";
const LAST_QUESTION_NAME = "propertyDetails";

const CREATE_AVM_VALUATION = gql`
  mutation CreateAvmValuationMutation($input: AvmValuationInput!) {
    createAvmValuation(input: $input) {
      successful
      failureReason
      avmValuationId
    }
  }
`;
const fireAnalyticsEvent = (value) => {
  const eventData = {
    event: AVM_SUCCESSFUL_VALUATION,
    type: FORM_TYPE,
    question: LAST_QUESTION_NAME,
    answer: undefined,
  };
  sendAnalyticsEvent({ ...eventData, ...value, answer: JSON.stringify(value) });
};

const shouldCreateDeal = ({
  contactDetails,
  customerIntent,
  urgency,
}: State): boolean => {
  if (customerIntent === "LOOKING_TO_BUY" || urgency === "NOT_SELLING") {
    return false;
  }

  return Boolean(contactDetails?.phone);
};

const shouldSkipPropertyDetails = (intent, urgency) => {
  if (intent === "ALREADY_ON_THE_MARKET") {
    return false;
  }
  if (urgency === "LESS_THAN_THREE_MONTHS") {
    return false;
  }
  return true;
};

export const OnlineValuationForm = () => {
  const [errorMessage, setErrorMessage] = useState<?React$Node>();
  const [createAvmValuation] = useMutation(CREATE_AVM_VALUATION);
  const dispatch = useDispatch();
  const history = useHistory();

  const { onSubmit: submitCustomerOnboarding } = usePersonalInformationOnSubmit(
    { leadScore: calculateAvmLeadScore },
  );

  const updateFormState = (payload: $Shape<State>) =>
    dispatch(formStateUpdateAction(payload));

  const onSubmit = async (state: State) => {
    try {
      const response = await createAvmValuation({
        variables: {
          input: {
            customer: {
              name: state.contactDetails?.name,
              email: state.contactDetails?.email,
              privacyPolicyConsent: state.contactDetails?.privacyPolicyConsent,
              customerIntent: state.customerIntent,
              urgency: state.urgency,
            },
            property: {
              addressId: state.address?.id,
              postcode: state.address?.postcode,
              propertyType: state.propertyDetails?.propertyType,
              bedrooms: state.propertyDetails?.bedrooms,
            },
          },
        },
      });
      const data = response?.data?.createAvmValuation;
      if (!data) {
        throw new Error("No data returned by graphql");
      }

      const { successful, failureReason, avmValuationId } = data;

      if (successful) {
        await handleValuationSuccess(state, avmValuationId);
        return;
      }

      if (failureReason === "EMAIL_INVALID") {
        setErrorMessage("The email address you have submitted is not valid.");
        return;
      }

      if (failureReason === "NO_DATA") {
        setErrorMessage(
          <>
            Unfortunately, we don't have data on your property, however, you can{" "}
            <Link to="/get-started">book a time</Link> with a member of our team
            to receive an accurate, in-person valuation.
          </>,
        );
        return;
      }

      throw new Error(failureReason);
    } catch (e) {
      Sentry.captureException(e);
      setErrorMessage(
        "It looks like there was an error, please try again. If the problem persists please give us a call on 020 3858 0695.",
      );
    }
  };

  const handleValuationSuccess = async (state, avmValuationId) => {
    fireAnalyticsEvent({
      name: state.contactDetails?.name,
      email: state.contactDetails?.email,
      phone: state.contactDetails?.phone,
      privacyPolicyConsent: state.contactDetails?.privacyPolicyConsent,
    });

    const highIntentVal =
      state.urgency === "LESS_THAN_THREE_MONTHS" ||
      state.customerIntent === "ALREADY_ON_THE_MARKET";

    if (highIntentVal) {
      await submitCustomerOnboarding(
        { ...state, avmValuationId },
        `/get-started/personalised-valuation-result/${avmValuationId}`,
      );
      return;
    }

    updateFormState({ avmValuationId });

    if (shouldCreateDeal(state)) {
      await submitCustomerOnboarding(
        { ...state, avmValuationId },
        `/get-started/valuation-result/${avmValuationId}`,
      );
    }

    history.push(`/get-started/valuation-result/${avmValuationId}`);
  };

  return (
    <>
      <MultiStageForm
        onSubmit={onSubmit}
        submitError={errorMessage}
        setSubmitError={setErrorMessage}
        formType={FORM_TYPE}
        lastButtonLabel="Show my results"
        restartPath="/get-started/online-valuation"
      >
        <Question
          route="/get-started/online-valuation"
          component={Address}
          name="address"
        />
        <Question
          route="/get-started/online-valuation/where-in-your-home-move"
          component={ValuationIntent}
          name="customerIntent"
        />
        <Question
          route="/get-started/online-valuation/when-will-you-list"
          component={ValuationUrgency}
          name="urgency"
          skip={({ customerIntent }) =>
            customerIntent === "ALREADY_ON_THE_MARKET" ||
            customerIntent === "LOOKING_TO_BUY"
          }
        />
        <Question
          route="/get-started/online-valuation/contact-details"
          component={ContactDetails}
          name="contactDetails"
        />
        <Question
          route="/get-started/online-valuation/property-details"
          component={PropertyDetails}
          name={LAST_QUESTION_NAME}
          skip={({ urgency, customerIntent }) =>
            shouldSkipPropertyDetails(customerIntent, urgency)
          }
        />
      </MultiStageForm>
    </>
  );
};
